// src/components/DeleteAssessmentModal.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Stack,
  TextInput,
  Button,
  Group,
  Text,
  Alert,
  Radio,
  Box,
  Badge,
  Divider
} from '@mantine/core';
import { IconTrash, IconAlertTriangle, IconArchive, IconX } from '@tabler/icons-react';

/**
 * DeleteAssessmentModal - Modal for soft or hard deleting assessments
 * Features:
 * - Soft delete (archive) by default for all users
 * - Hard delete option for Risk Managers only
 * - Dependency check (blocks deletion if Explorer assessments depend on Builder)
 * - Confirmation input for hard delete
 * - 30-day recovery window messaging for soft delete
 */
function DeleteAssessmentModal({
  opened,
  onClose,
  assessment,
  userIsRiskManager = false,
  dependentAssessments = [],
  onDelete
}) {
  const [deletionType, setDeletionType] = useState('soft');
  const [confirmationText, setConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (opened) {
      setDeletionType('soft');
      setConfirmationText('');
    }
  }, [opened]);

  const hasDependencies = dependentAssessments && dependentAssessments.length > 0;
  const isHardDelete = deletionType === 'hard';
  const isBuilderWithExplorer = assessment?.assessment_type === 'builder' && hasDependencies;

  // Validation for hard delete
  const isConfirmationValid = !isHardDelete || confirmationText === assessment?.assessment_name;
  const canSubmit = isConfirmationValid && !isDeleting && !isBuilderWithExplorer;

  const handleDelete = async () => {
    if (!canSubmit) return;

    setIsDeleting(true);
    try {
      await onDelete({
        assessmentUuid: assessment.assessment_uuid,
        deletionType,
        confirmationText: isHardDelete ? confirmationText : undefined
      });
      onClose();
    } catch (error) {
      console.error('[DeleteAssessmentModal] Error deleting assessment:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      setConfirmationText('');
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          {isHardDelete ? <IconTrash size={20} /> : <IconArchive size={20} />}
          <Text fw={600}>
            {isHardDelete ? 'Permanently Delete Assessment' : 'Archive Assessment'}
          </Text>
        </Group>
      }
      size="lg"
    >
      <Stack gap="md">
        {/* Warning Banner */}
        {isHardDelete ? (
          <Alert
            icon={<IconAlertTriangle size={20} />}
            title="Warning: Permanent Deletion"
            color="red"
            variant="filled"
          >
            This action CANNOT be undone. All data will be permanently deleted, including risks,
            actions, controls, and embeddings.
          </Alert>
        ) : (
          <Alert
            icon={<IconArchive size={20} />}
            title="Archive Assessment"
            color="blue"
            variant="light"
          >
            This assessment will be archived for 30 days. You can restore it during this period.
          </Alert>
        )}

        {/* Dependency Warning */}
        {isBuilderWithExplorer && (
          <Alert
            icon={<IconAlertTriangle size={20} />}
            title="Cannot Delete: Active Dependencies"
            color="orange"
            variant="filled"
          >
            <Stack gap="xs">
              <Text size="sm">
                This Builder assessment has {dependentAssessments.length} Explorer assessment(s)
                that depend on it:
              </Text>
              <Box ml="md">
                {dependentAssessments.map((dep, idx) => (
                  <Text key={idx} size="sm">
                    • {dep.assessment_name}
                  </Text>
                ))}
              </Box>
              <Text size="sm">
                Please delete the Explorer assessments first, or archive this Builder assessment
                instead.
              </Text>
            </Stack>
          </Alert>
        )}

        <Divider />

        {/* Assessment Info */}
        <Box>
          <Text size="sm" c="dimmed" mb="xs">
            Assessment Details:
          </Text>
          <Stack gap="xs">
            <Group gap="xs">
              <Text size="sm" fw={600}>
                Name:
              </Text>
              <Text size="sm">{assessment?.assessment_name || 'Unnamed Assessment'}</Text>
              <Badge size="xs" color={assessment?.assessment_type === 'builder' ? 'blue' : 'green'}>
                {assessment?.assessment_type || 'builder'}
              </Badge>
            </Group>
            <Group gap="xs">
              <Text size="sm" fw={600}>
                UUID:
              </Text>
              <Text size="sm" c="dimmed" ff="monospace">
                {assessment?.assessment_uuid}
              </Text>
            </Group>
            {assessment?.owner_name && (
              <Group gap="xs">
                <Text size="sm" fw={600}>
                  Owner:
                </Text>
                <Text size="sm">{assessment.owner_name}</Text>
              </Group>
            )}
          </Stack>
        </Box>

        <Divider />

        {/* Deletion Type Selector - Only for Risk Managers */}
        {userIsRiskManager && !isBuilderWithExplorer && (
          <>
            <Radio.Group
              value={deletionType}
              onChange={setDeletionType}
              label="Deletion Type"
              description="Choose how you want to delete this assessment"
            >
              <Stack gap="xs" mt="xs">
                <Radio
                  value="soft"
                  label="Archive (Recommended)"
                  description="Mark as deleted, keep all data, 30-day recovery window"
                />
                <Radio
                  value="hard"
                  label="Permanent Delete"
                  description="Completely remove from database - CANNOT be undone"
                />
              </Stack>
            </Radio.Group>
            <Divider />
          </>
        )}

        {/* Confirmation Input for Hard Delete */}
        {isHardDelete && !isBuilderWithExplorer && (
          <TextInput
            label="Type the assessment name to confirm permanent deletion"
            placeholder={assessment?.assessment_name}
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            error={
              confirmationText && confirmationText !== assessment?.assessment_name
                ? 'Assessment name does not match'
                : undefined
            }
            required
            autoFocus
            data-autofocus
            description={`Type exactly: "${assessment?.assessment_name}"`}
          />
        )}

        {/* Action Buttons */}
        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={handleClose} disabled={isDeleting} leftSection={<IconX size={16} />}>
            Cancel
          </Button>
          <Button
            color={isHardDelete ? 'red' : 'blue'}
            onClick={handleDelete}
            disabled={!canSubmit}
            loading={isDeleting}
            leftSection={isHardDelete ? <IconTrash size={16} /> : <IconArchive size={16} />}
          >
            {isHardDelete ? 'Permanently Delete' : 'Archive Assessment'}
          </Button>
        </Group>

        {/* Help Text */}
        {!isBuilderWithExplorer && (
          <Text size="xs" c="dimmed" ta="center">
            {isHardDelete
              ? 'All risks, actions, controls, and embeddings will be permanently deleted.'
              : 'Archived assessments can be restored from the "Deleted" tab within 30 days.'}
          </Text>
        )}
      </Stack>
    </Modal>
  );
}

DeleteAssessmentModal.propTypes = {
  opened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  assessment: PropTypes.shape({
    assessment_uuid: PropTypes.string.isRequired,
    assessment_name: PropTypes.string,
    assessment_type: PropTypes.string,
    owner_name: PropTypes.string
  }),
  userIsRiskManager: PropTypes.bool,
  dependentAssessments: PropTypes.arrayOf(
    PropTypes.shape({
      assessment_uuid: PropTypes.string,
      assessment_name: PropTypes.string
    })
  ),
  onDelete: PropTypes.func.isRequired
};

export default DeleteAssessmentModal;
